import React, { FunctionComponent } from "react";

import { NumberOfRegistrationsChart } from "./NumberOfRegistrationsChart";
import { useLimits } from "../../hooks/useLimits";
import { useRegistrations } from "../../hooks/useRegistrations";
import { useAuthentication } from "../../hooks/useAuthentication";
import { useAuthUser } from "../../hooks/useAuthUser";
import { EmailVerifiedNotification } from "../common/EmailVerifiedNotification";
import { ContentLayout } from "../common/Layout";
import { Meter } from "../common/Meter";
import { useUserIdParam } from "../../hooks/useUserIdParam";

interface Props {
  isAuthenticated: boolean;
}

const formatCurrency = (amount: number): string => `CHF ${new Intl.NumberFormat("de-CH").format(amount)}`;

const Dashboard: FunctionComponent<Props> = ({ isAuthenticated }) => {
  const userId = useUserIdParam();
  useAuthentication(isAuthenticated);
  const { user } = useAuthUser();
  const [registrations, registrationsLoading, registrationsError] = useRegistrations(userId);
  const [limits, limitsLoading, limitsError] = useLimits(userId);

  const isLoading = registrationsLoading || limitsLoading;
  const isError = registrationsError || limitsError;

  const totalRegistrations = registrations?.length || 0;
  const totalLimits = limits?.length || 0;
  const totalTaken = limits?.reduce((sum, limit) => sum + (limit.taken || 0), 0) || 0;
  const fullyBookedLimits = limits?.filter((limit) => limit.taken >= limit.limit).length || 0;
  const totalCapacity = limits?.reduce((sum, limit) => sum + (limit.limit || 0), 0) || 0;
  const utilizationRate = totalCapacity > 0 ? Math.round((totalTaken / totalCapacity) * 100) : 0;
  const totalRevenue = limits?.reduce((sum, limit) => sum + (limit.price || 0) * (limit.taken || 0), 0) || 0;

  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const registrationsLast30Days =
    registrations?.filter((registration) => registration.creation.createdAt.getTime() >= thirtyDaysAgo).length || 0;

  const kpis = [
    { value: String(totalRegistrations), label: "Anmeldungen" },
    { value: `${fullyBookedLimits}/${totalLimits}`, label: "Angebote voll" },
    { value: `${utilizationRate}%`, label: "Auslastung" },
    { value: new Intl.NumberFormat("de-CH").format(totalRevenue), label: "Umsatz CHF" }
  ];

  if (isError) return <p>Fehler beim Laden der Daten</p>;

  return (
    <ContentLayout
      siteTitle={"Spielgruppe Online - Dashboard"}
      title={"Dashboard"}
      subtitle={`${totalRegistrations} Anmeldungen`}
      isLoading={isLoading}>
      <EmailVerifiedNotification className={"mb-3"} user={user} />

      <div className="dashboard-layout">
        <div className="kpi-grid">
          {kpis.map((kpi, index) => (
            <div className="kpi-tile" key={kpi.label}>
              <span className={`kpi-tile__dot kpi-tile__dot--c${index + 1}`} />
              <span className="kpi-tile__value">{kpi.value}</span>
              <span className="kpi-tile__label">{kpi.label}</span>
            </div>
          ))}
        </div>

        <div className="dashboard-columns">
          <section className="panel">
            <div className="panel__head">
              <h2>Angebote im Detail</h2>
              <span className="panel__meta">
                {totalLimits} Angebote · {fullyBookedLimits} ausgebucht
              </span>
            </div>
            {limits.length === 0 && <div className="panel__body text-muted">Noch keine Angebote erfasst.</div>}
            <div className="data-rows">
              {limits.map((limit) => (
                <div className="data-row data-row--offer" key={limit.property}>
                  <div className="data-row__primary">{limit.property}</div>
                  <Meter taken={limit.taken || 0} total={limit.limit || 0} />
                  <span className="data-row__count">{limit.taken || 0} Anm.</span>
                  <span className={`data-row__money${limit.taken ? "" : " is-zero"}`}>
                    {formatCurrency((limit.price || 0) * (limit.taken || 0))}
                  </span>
                </div>
              ))}
            </div>
          </section>

          <section className="panel">
            <div className="panel__head">
              <h2>Anmeldungen Verlauf</h2>
              <span className="panel__meta">Kumuliert</span>
            </div>
            <div className="panel__body">
              <NumberOfRegistrationsChart registrations={registrations} />
            </div>
            <div className="panel__foot">
              <div>
                <div className="panel__stat">{totalRegistrations}</div>
                <div className="panel__stat-label">Total</div>
              </div>
              <div>
                <div className="panel__stat">+{registrationsLast30Days}</div>
                <div className="panel__stat-label">Letzte 30 Tage</div>
              </div>
              <div>
                <div className="panel__stat">{utilizationRate}%</div>
                <div className="panel__stat-label">Auslastung</div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </ContentLayout>
  );
};

export default Dashboard;
